// Every click of the funnel, with where it landed (owner-directed 2026-10-20).
//
// WHY THIS EXISTS. Clarity records the same thing and more, but its player refuses to be
// embedded (`X-Frame-Options: SAMEORIGIN`, measured 2026-10-20) and needs a Microsoft account
// to watch. What the admin already owns is the journey, gesture by gesture; what it could
// never show is WHERE on the page someone pressed. This is that, in our own tables.
//
// ⚠ THE FUNNEL ONLY, NEVER THE CLIENT SPACE. The gate is the registry's own `analytics` flag
// — true for the six funnel pages, false for `espace-client` (core/main.js calls this where it
// starts the journal). A client reading their invoices is not a prospect being measured, and
// the one place that decides which pages are the funnel is the registry (golden rule #1).
//
// ⚠ GROUPED, NOT ONE ROW PER CLICK (owner's call, 2026-10-20). A full journey is fifty to two
// hundred clicks; a row each would multiply `nb_prospect_events` by three or four and bury the
// admin's Parcours détaillé under them. Nothing is lost — every click keeps its position and
// its instant — they simply travel together.
//
// WHAT TRAVELS, AND WHAT NEVER DOES. A point carries where (`x`, `y`), when (`t`), and WHAT
// was pressed, as a short structural descriptor: the tag, plus the hook the code itself uses
// (`data-…`) or the field's NAME. Never a field's value, never the text of what was clicked —
// a journal is no place for what someone typed, and a label can hold a name as easily as a
// word.

import { note } from '/shared/analytics/prospection.js';
import { currentStep } from '/shared/analytics/funnelStep.js';

/**
 * Document coordinates, so a point means the same thing after a scroll — `pageX/pageY` already
 * fold the scroll offset in. The viewport's size travels with the batch, because a coordinate
 * without the surface it was measured on cannot be replayed anywhere.
 */
const POINT_MAX = 60;
/**
 * Sixty points fit comfortably under the server's 4 KB ceiling for an ordinary gesture
 * (NB_Backend, services/prospects.js): about 50 bytes each, so roughly 3 KB with room to
 * spare. Past that the batch leaves and a new one starts — the cap bounds a payload, it never
 * drops a click.
 */
const DESCRIPTOR_MAX = 40;
// The attributes worth naming, in order: the hooks this codebase actually steers by, then the
// field's name. `value`, `placeholder` and text are deliberately absent.
const TELLING = ['data-press', 'data-download', 'data-legal', 'data-camera', 'name', 'id'];

let points = [];
let started = 0;

/**
 * Start following the clicks of this page.
 *
 * ONE LISTENER, ON THE DOCUMENT, IN THE CAPTURE PHASE: it sees a press even when the handler
 * underneath stops the event, and it costs one subscription rather than one per control.
 * Passive, so it can never delay a tap on a phone.
 *
 * `click` covers both halves of the ask: a tap on a touch screen dispatches one too, after
 * the touch sequence, with the same coordinates.
 *
 * @returns {void}
 */
export function initClickTrail() {
  started = Date.now();
  document.addEventListener('click', record, { capture: true, passive: true });
  // The last batch of a page would otherwise never leave: the queue it feeds flushes on the
  // same two events, so this has to put its points in before that happens.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') send();
  });
  window.addEventListener('pagehide', send);
}

/** @param {MouseEvent} event */
function record(event) {
  // A click the page itself dispatched (`el.click()`) has no position and never happened on
  // screen: keeping it would put a phantom press at the top-left corner of every replay.
  if (!event.isTrusted) return;
  points.push({
    x: Math.round(event.pageX),
    y: Math.round(event.pageY),
    // Milliseconds since this page started following, so a batch reads as a sequence rather
    // than as a column of wall-clock stamps.
    t: Date.now() - started,
    e: describe(event.target),
  });
  if (points.length >= POINT_MAX) send();
}

/**
 * What was pressed, structurally.
 *
 * @param {EventTarget|null} target
 * @returns {string} e.g. `button[data-press]`, `input[name=email]`, or just `div`
 */
function describe(target) {
  const el = target instanceof Element ? target : null;
  if (!el) return '';
  const tag = el.tagName.toLowerCase();
  for (const attr of TELLING) {
    if (!el.hasAttribute(attr)) continue;
    const value = el.getAttribute(attr);
    // A bare hook (`data-press`) is worth more as a name than as an empty pair.
    const part = value ? `${attr}=${value}` : attr;
    return `${tag}[${part}]`.slice(0, DESCRIPTOR_MAX);
  }
  return tag.slice(0, DESCRIPTOR_MAX);
}

/** Hand the points over, if there are any. */
function send() {
  if (!points.length) return;
  const batch = points;
  points = [];
  // ⚠ THE STEP IS READ NOW, NOT AT START-UP: the estimate page becomes the capture step
  // without reloading, so a step chosen when the listener was wired would label half the
  // journey wrongly. A batch that straddles the change takes the step it ended on — each
  // point still carries its own `t`, which places it against the rest of the journey.
  note(
    'clics',
    {
      points: batch,
      // The surface they were measured on. Without it the coordinates describe nothing.
      w: window.innerWidth,
      h: window.innerHeight,
    },
    { page: currentStep() }
  );
}
